/**
 * Centralized enum containing most supported media types across the application.
 * Includes both media forms (e.g., podcast, radio, tv) and social media platforms.
 * All enum values are backend-compatible and are broadly used throughout different
 * areas of the code base.
 */
export enum MediaTypeEnum {
    ONLINE_NEWS = 'ONLINE_NEWS',
    PRINT = 'PRINT',
    BLOGS = 'BLOGS',
    PRESS_RELEASE = 'PRESS_RELEASE',
    TV = 'TV',
    RADIO = 'RADIO',
    FACEBOOK = 'FACEBOOK',
    INSTAGRAM = 'INSTAGRAM',
    TWITTER = 'TWITTER',
    YOUTUBE = 'YOUTUBE',
    REDDIT = 'REDDIT',
    PODCAST = 'PODCAST',
    MAGAZINE = 'MAGAZINE',
    TIKTOK = 'TIKTOK',
    SNAPCHAT = 'SNAPCHAT',
    THREADS = 'THREADS',
    LINKEDIN = 'LINKEDIN',
    BLUESKY = 'BLUESKY',
    WECHAT = 'WECHAT',
    SINA_WEIBO = 'SINA_WEIBO',
    TWITCH = 'TWITCH',
    PINTEREST = 'PINTEREST',
    TUMBLR = 'TUMBLR',
    TELEGRAM = 'TELEGRAM',
    WHATSAPP = 'WHATSAPP',
    VK = 'VK',
    OTHER = 'OTHER',


    // TODO: if I add it to MediaTypeEnum list, it breaks parts of the apps because we do Object.values(MediaTypeEnum) or getAllMediaTypes in a few places
    // LLM_RESPONSE = 'LLM_RESPONSE'
}


export const mediaTypeNoHeadlineMap = new Map<MediaTypeEnum, string>([
    [ MediaTypeEnum.ONLINE_NEWS,   'Mention.noHeadline'        ],
    [ MediaTypeEnum.PRINT,         'Mention.noHeadline'        ],
    [ MediaTypeEnum.BLOGS,         'Mention.noHeadline'        ],
    [ MediaTypeEnum.PRESS_RELEASE, 'Mention.noHeadline'        ],
    [ MediaTypeEnum.TV,            'Mention.noHeadline'        ],
    [ MediaTypeEnum.RADIO,         'Mention.noHeadline'        ],
    [ MediaTypeEnum.FACEBOOK,      'Mention.faceBookHeadline'  ],
    [ MediaTypeEnum.INSTAGRAM,     'Mention.instagramHeadline' ],
    [ MediaTypeEnum.TWITTER,       'Mention.twitterHeadline'   ],
    [ MediaTypeEnum.YOUTUBE,       'Mention.youtubeHeadline'   ],
    [ MediaTypeEnum.REDDIT,        'Mention.noHeadline'        ],
    [ MediaTypeEnum.PODCAST,       'Mention.noHeadline'        ],
    [ MediaTypeEnum.MAGAZINE,      'Mention.noHeadline'        ],
    [ MediaTypeEnum.TIKTOK,        'Mention.tiktokHeadline'    ],
    [ MediaTypeEnum.SNAPCHAT,      'Mention.snapchatHeadline'  ],
    [ MediaTypeEnum.THREADS,       'Mention.threadsHeadline'   ],
    [ MediaTypeEnum.LINKEDIN,      'Mention.linkedinHeadline'  ],
    [ MediaTypeEnum.BLUESKY,       'Mention.blueskyHeadline'   ],
    [ MediaTypeEnum.WECHAT,        'Mention.wechatHeadline'    ],
    [ MediaTypeEnum.SINA_WEIBO,    'Mention.sinaweiboHeadline' ],
    [ MediaTypeEnum.TWITCH,        'Mention.twitchHeadline'    ],
    [ MediaTypeEnum.PINTEREST,     'Mention.pinterestHeadline' ],
    [ MediaTypeEnum.TUMBLR,        'Mention.tumblrHeadline'    ],
    [ MediaTypeEnum.TELEGRAM,      'Mention.telegramHeadline'  ],
    [ MediaTypeEnum.WHATSAPP,      'Mention.whatsappHeadline'  ],
    [ MediaTypeEnum.VK,            'Mention.vkHeadline'        ],
    [ MediaTypeEnum.OTHER,         'Mention.noHeadline'        ],
]);

export const mediaTypeIconMap = new Map<MediaTypeEnum, string>([
    [ MediaTypeEnum.ONLINE_NEWS,   'fa-solid fa-globe'        ],
    [ MediaTypeEnum.PRINT,         'fa-solid fa-newspaper'    ],
    [ MediaTypeEnum.BLOGS,         'fa-solid fa-message-pen'  ],
    [ MediaTypeEnum.PRESS_RELEASE, 'fa-solid fa-bullhorn'     ],
    [ MediaTypeEnum.TV,            'fa-solid fa-tv-retro'     ],
    [ MediaTypeEnum.RADIO,         'fa-solid fa-tower-cell'   ],
    [ MediaTypeEnum.FACEBOOK,      'fa-brands fa-facebook'    ],
    [ MediaTypeEnum.INSTAGRAM,     'fa-brands fa-instagram'   ],
    [ MediaTypeEnum.TWITTER,       'fa-brands fa-x-twitter'   ],
    [ MediaTypeEnum.YOUTUBE,       'fa-brands fa-youtube'     ],
    [ MediaTypeEnum.REDDIT,        'fa-brands fa-reddit'      ],
    [ MediaTypeEnum.PODCAST,       'fa-solid fa-podcast'      ],
    [ MediaTypeEnum.MAGAZINE,      'fa-kit fa-agpr-magazine'  ],
    [ MediaTypeEnum.TIKTOK,        'fa-brands fa-tiktok'      ],
    [ MediaTypeEnum.SNAPCHAT,      'fa-brands fa-snapchat'    ],
    [ MediaTypeEnum.THREADS,       'fa-brands fa-threads'     ],
    [ MediaTypeEnum.LINKEDIN,      'fa-brands fa-linkedin'    ],
    [ MediaTypeEnum.BLUESKY,       'fa-brands fa-bluesky'     ],
    [ MediaTypeEnum.WECHAT,        'fa-brands fa-weixin'      ],
    [ MediaTypeEnum.SINA_WEIBO,    'fa-brands fa-weibo'       ],
    [ MediaTypeEnum.TWITCH,        'fa-brands fa-twitch'      ],
    [ MediaTypeEnum.PINTEREST,     'fa-brands fa-pinterest'   ],
    [ MediaTypeEnum.TUMBLR,        'fa-brands fa-tumblr'      ],
    [ MediaTypeEnum.TELEGRAM,      'fa-brands fa-telegram'    ],
    [ MediaTypeEnum.WHATSAPP,      'fa-brands fa-whatsapp'    ],
    [ MediaTypeEnum.VK,            'fa-brands fa-vk'          ],
    [ MediaTypeEnum.OTHER,         'fa-solid fa-circle-nodes' ],
]);

/**
 * Since moving from lower case enum notation to upper case, we need to provide
 * backward compatibility for existing saved settings such as UserAppPreferenceChartColors
 */
export const mediaTypeColorKeyMap = new Map<MediaTypeEnum, string>([
    [ MediaTypeEnum.ONLINE_NEWS,   'onlineNews'   ],
    [ MediaTypeEnum.PRINT,         'print'        ],
    [ MediaTypeEnum.BLOGS,         'blogs'        ],
    [ MediaTypeEnum.PRESS_RELEASE, 'pressRelease' ],
    [ MediaTypeEnum.TV,            'TV'           ],
    [ MediaTypeEnum.RADIO,         'radio'        ],
    [ MediaTypeEnum.FACEBOOK,      'facebook'     ],
    [ MediaTypeEnum.INSTAGRAM,     'instagram'    ],
    [ MediaTypeEnum.TWITTER,       'twitter'      ],
    [ MediaTypeEnum.YOUTUBE,       'youtube'      ],
    [ MediaTypeEnum.REDDIT,        'reddit'       ],
    [ MediaTypeEnum.PODCAST,       'podcast'      ],
    [ MediaTypeEnum.MAGAZINE,      'magazine'     ],
    [ MediaTypeEnum.TIKTOK,        'tiktok'       ],
    [ MediaTypeEnum.SNAPCHAT,      'snapchat'     ],
    [ MediaTypeEnum.THREADS,       'threads'      ],
    [ MediaTypeEnum.LINKEDIN,      'linkedin'     ],
    [ MediaTypeEnum.BLUESKY,       'bluesky'      ],
    [ MediaTypeEnum.WECHAT,        'wechat'       ],
    [ MediaTypeEnum.SINA_WEIBO,    'sinaWeibo'    ],
    [ MediaTypeEnum.TWITCH,        'twitch'       ],
    [ MediaTypeEnum.PINTEREST,     'pinterest'    ],
    [ MediaTypeEnum.TUMBLR,        'tumblr'       ],
    [ MediaTypeEnum.TELEGRAM,      'telegram'     ],
    [ MediaTypeEnum.WHATSAPP,      'whatsapp'     ],
    [ MediaTypeEnum.VK,            'vk'           ],
    [ MediaTypeEnum.OTHER,         'other'        ],
]);

export const mediaTypeColorKeyReverseMap = new Map(
    Array.from(mediaTypeColorKeyMap.entries()).map(([key, value]) => [value, key])
);


export type SocialMediaType =
    MediaTypeEnum.FACEBOOK |
    MediaTypeEnum.TWITTER |
    MediaTypeEnum.INSTAGRAM |
    MediaTypeEnum.YOUTUBE |
    MediaTypeEnum.REDDIT |
    MediaTypeEnum.TIKTOK |
    MediaTypeEnum.LINKEDIN |
    MediaTypeEnum.BLUESKY;

export const socialMediaTypeImageMap = new Map<SocialMediaType, string>([
    [ MediaTypeEnum.FACEBOOK,  'assets/images/facebook_logo.png'  ],
    [ MediaTypeEnum.TWITTER,   'assets/images/twitter_logo.png'   ],
    [ MediaTypeEnum.INSTAGRAM, 'assets/images/instagram_logo.png' ],
    [ MediaTypeEnum.YOUTUBE,   'assets/images/youtube_logo.png'   ],
    [ MediaTypeEnum.REDDIT,    'assets/images/reddit_logo.png'    ],
    [ MediaTypeEnum.TIKTOK,    'assets/images/tiktok_logo.png'    ],
    [ MediaTypeEnum.LINKEDIN,  'assets/images/linkedin_logo.png'  ],
    [ MediaTypeEnum.BLUESKY,   'assets/images/bluesky_logo.png'   ],
]);

/** Returns list of Media Types that are supported in Social */
export const getSocialMediaTypes = (): SocialMediaType[] =>  {
    return [
        MediaTypeEnum.FACEBOOK,
        MediaTypeEnum.INSTAGRAM,
        MediaTypeEnum.TWITTER,
        MediaTypeEnum.YOUTUBE,
        MediaTypeEnum.REDDIT,
        MediaTypeEnum.TIKTOK,
        MediaTypeEnum.LINKEDIN,
        MediaTypeEnum.BLUESKY
    ];
};


/** Returns list of Media Types that are considered Social Media Platforms */
export const getSocialMediaPlatformTypes = (): MediaTypeEnum[] =>  {
    return [
        MediaTypeEnum.FACEBOOK,
        MediaTypeEnum.TWITTER,
        MediaTypeEnum.INSTAGRAM,
        MediaTypeEnum.YOUTUBE,
        MediaTypeEnum.REDDIT,
        MediaTypeEnum.TIKTOK,
        MediaTypeEnum.SNAPCHAT,
        MediaTypeEnum.THREADS,
        MediaTypeEnum.LINKEDIN,
        MediaTypeEnum.BLUESKY,
        MediaTypeEnum.WECHAT,
        MediaTypeEnum.SINA_WEIBO,
        MediaTypeEnum.TWITCH,
        MediaTypeEnum.PINTEREST,
        MediaTypeEnum.TUMBLR,
        MediaTypeEnum.TELEGRAM,
        MediaTypeEnum.WHATSAPP,
        MediaTypeEnum.VK,
        MediaTypeEnum.OTHER
    ];
};

export const getSavedSearchMediaTypes = (): MediaTypeEnum[] => {
    return [
        MediaTypeEnum.MAGAZINE,
        MediaTypeEnum.SNAPCHAT,
        MediaTypeEnum.THREADS,
        MediaTypeEnum.WECHAT,
        MediaTypeEnum.SINA_WEIBO,
        MediaTypeEnum.TWITCH,
        MediaTypeEnum.PINTEREST,
        MediaTypeEnum.TUMBLR,
        MediaTypeEnum.TELEGRAM,
        MediaTypeEnum.WHATSAPP,
        MediaTypeEnum.VK,
        MediaTypeEnum.OTHER,
    ];
};


export const getAllMediaTypes = (): MediaTypeEnum[] => {
    return Object.values(MediaTypeEnum) as MediaTypeEnum[];
};

